import { del, get, patch, post, uploadFile } from 'src/functions';
import { distancesMap, refreshProbPercMap, similaritiesMap } from './repository';

const ONPOINT_AUTH = process.env.REACT_APP_ONPOINT_AUTH || 'http://localhost:9000';
const ONPOINT = process.env.REACT_APP_ONPOINT || 'http://localhost';

function AuthException(code, message) {
  this.code = code;
  this.message = message;
}

// AUTH

export const inlineLogin = async (username, password) => {
  const resp = await fetch(`${ONPOINT_AUTH}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });
  if (resp.status <= 399) {
    return resp.json();
  }
  if (resp.status <= 499) {
    const data = await resp.json();
    throw new AuthException(
      data.code,
      data.message || 'System is unable to process your request. Please try again later'
    );
  } else {
    throw new Error('System is unable to process your request. Please try again later');
  }
};

export const inlineAuthChallenge = async (challengeName, session, username, password) => {
  const resp = await fetch(`${ONPOINT_AUTH}/authChallenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      session,
      challengeName,
      challengeResponses: {
        USERNAME: username,
        NEW_PASSWORD: password,
      },
    }),
  });
  if (resp.status <= 399) {
    return resp.json();
  }
  if (resp.status <= 499) {
    const data = await resp.json();
    throw new Error(data.message || 'System is unable to process your request. Please try again later');
  } else {
    throw new Error('System is unable to process your request. Please try again later');
  }
};

export const forgotPassword = async (username) => {
  const resp = await fetch(`${ONPOINT_AUTH}/forgotPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ username }),
  });
  if (resp.status <= 399) {
    // NOTE: No longer take JSON response into account to obfuscate
    // existence of registered users
    return;
  }
  if (resp.status <= 499) {
    const data = await resp.json();
    throw new Error(data.message || 'System is unable to process your request. Please try again later');
  } else {
    throw new Error('System is unable to process your request. Please try again later');
  }
};

export const changePassword = async (confirmationCode, username, password) => {
  const resp = await fetch(`${ONPOINT_AUTH}/confirmForgotPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ confirmationCode, username, password }),
  });
  if (resp.status <= 399) {
    return resp.json();
  }
  if (resp.status <= 499) {
    const data = await resp.json();
    throw new Error(data.message || 'System is unable to process your request. Please try again later');
  } else {
    throw new Error('System is unable to process your request. Please try again later');
  }
};

export const resendInvitation = async (username) => post(`${ONPOINT_AUTH}/resendInvitation`, { username });

export const resetPassword = async (username) => post(`${ONPOINT_AUTH}/resetPassword`, { username });

export const inlineLogout = async () => {
  await post(`${ONPOINT_AUTH}/logout`, {});
};

// Frameworks

export const fetchFrameworks = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/frameworks/?query=${JSON.stringify(query, null, 0)}&per_page=100`,
    options
  );
  return items;
};

export const fetchFrameworksBasic = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/frameworks/list?query=${JSON.stringify(query, null, 0)}&per_page=100`,
    options
  );
  return items;
};

export const createFramework = async (payload) => post(`${ONPOINT}/core/v1/frameworks`, payload);

export const editFramework = async (frameworkId, payload) =>
  patch(`${ONPOINT}/core/v1/frameworks/${frameworkId}`, payload);

export const deleteFramework = async (frameworkId) => del(`${ONPOINT}/core/v1/frameworks/${frameworkId}`);

export const checkDeleteFramework = async (frameworkId, query = {}, options = {}) =>
  get(
    `${ONPOINT}/core/v1/frameworks/${frameworkId}/service/can_delete?query=${JSON.stringify(
      query,
      null,
      0
    )}&per_page=100`,
    options
  );

// Refreshes

export const fetchRefreshes = async (query = {}, options = {}) => {
  const { items } = await get(`${ONPOINT}/core/v1/refreshes/?query=${JSON.stringify(query, null, 0)}`, options);
  return items;
};

// Real time api
export const fetchRealTimeApiKey = async () => get(`${ONPOINT_AUTH}/integrations/scores_api/getApiKey`);

export const enableRealTimeApi = async (payload) =>
  post(`${ONPOINT_AUTH}/integrations/scores_api/enableClient`, payload);

export const disableRealTimeApi = async (payload) =>
  post(`${ONPOINT_AUTH}/integrations/scores_api/disableClient`, payload);

export const demoRealTimeApiV2 = async (configId, payload) => {
  const casedPayload = {
    ...payload,
    first_name: payload.firstName,
    last_name: payload.lastName,
  };
  const { data } = await post(`${ONPOINT_AUTH}/integrations/scores_api/demo/${configId}`, casedPayload);
  return data;
};

/**
 * fetchRttEntries - Fetch a list of return time entries entries
 *
 * @param {object} query
 * @returns TrackingEntry[]
 */
export const fetchRttEntries = async (query = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/realtime_api/monitoring?query=${JSON.stringify(query, null, 0)}&per_page=${5000}`
  );
  return items;
};

/**
 * fetchTrackingEntries - Fetch a list of tracking api entries
 *
 * @param {object} query
 * @returns TrackingEntry[]
 */
export const fetchTrackingEntries = async (query = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v2/tracking/api?query=${JSON.stringify(query, null, 0)}&per_page=${5000}`
  );
  return items;
};

/**
 * fetchRtConfigs - Fetch a list of CS entries
 *
 * @param {object} query
 * @returns RtCs[]
 */
export const fetchRtConfigs = async (query = {}) => {
  const { items } = await get(`${ONPOINT}/core/v1/realtime_api/?query=${JSON.stringify(query, null, 0)}&per_page=100`);
  return items;
};

/**
 * fetchRtConfig - Fetch a CS entry
 *
 * @param {string} configId
 * @returns RtCs
 */
export const fetchRtConfig = async (configId = '') => get(`${ONPOINT}/core/v1/realtime_api/${configId}`);

/**
 * createRtConfigStatus - Create a RT config status
 *
 * @param {string} configId
 * @param {object} payload
 * @returns any
 */
export const createRtConfigStatus = async (configId = '', payload = {}) =>
  post(`${ONPOINT}/core/v1/realtime_api/${configId}/status`, payload);

/**
 * createRtConfig - Create a CS entry
 *
 * @param {RtCs} payload
 * @returns {object}
 */
export const createRtConfig = async (payload) => post(`${ONPOINT}/core/v1/realtime_api/`, payload);

/**
 * deleteRtConfig - Delete a CS entry
 *
 * @param {string} configId
 * @returns {any}
 */
export const deleteRtConfig = async (configId = '') => del(`${ONPOINT}/core/v1/realtime_api/${configId}`);

/**
 * editRtConfig - Edit a CS entry
 *
 * @param {string} configId
 * @param {object} payload
 * @returns {object}
 */
export const editRtConfig = async (configId, payload) => patch(`${ONPOINT}/core/v1/realtime_api/${configId}`, payload);

// Traits

export const getTraits = async () => {
  const { items } = await get(`${ONPOINT}/core/v1/traits/?per_page=100`);
  return items;
};

// Word 2 PX

export const getWordVector = async (word, includeRelated = false, wvmId = 2) =>
  get(`${ONPOINT}/core/v1/word_vectors/${word}?wvm_id=${wvmId}&include_related=${includeRelated}`);

export const getWordVectors = async (line, wvmId = 2) =>
  get(`${ONPOINT}/core/v1/word_vectors?line=${line}&wvm_id=${wvmId}`);

export const getWordModel = async (refresh = 'RF32', setId = 1) =>
  get(`${ONPOINT}/core/v1/word_models?refresh_name=${refresh}&set_id=${setId}`);

export const getRelatedWords = async (word, wvmId = 1) =>
  get(`${ONPOINT}/core/v1/word_related/${word}?wvm_id=${wvmId}`);

export const getMaskedWords = async (sequence, wvmId = 1, limit = 20) =>
  get(`${ONPOINT}/core/v1/word_mask/?sequence=${sequence}&wvm_id=${wvmId}&limit=${limit}`);

// Refresh Percentage Percentile Map

export const getPercentile = async (query = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/refreshes_probability_percentile_map/?query=${JSON.stringify(query, null, 0)}&per_page=50`
  );
  return items;
};

export const getPercentileFromMap = (refreshId, traitId, probability) =>
  refreshProbPercMap?.[refreshId]?.find?.(({ t, p }) => t === traitId && p === probability)?.q;

export const getWordDistancesFromMap = (wvmId, refreshId) => distancesMap[`${wvmId}-${refreshId}`];

export const getWordSimilaritiesFromMap = (wvmId, refreshId) => similaritiesMap[`${wvmId}-${refreshId}`];

export const getHighValueWords = (segmentId, clusterId, wvmId = 1) =>
  clusterId
    ? get(`${ONPOINT}/core/v1/word_high_value?wvm_id=${wvmId}&segment_id=${segmentId}&cluster_id=${clusterId}`)
    : get(`${ONPOINT}/core/v1/word_high_value?wvm_id=${wvmId}&segment_id=${segmentId}`);

export const generateHighValueWords = (segmentId, clusterId, wvmId = 1) =>
  clusterId
    ? post(`${ONPOINT}/core/v1/word_high_value?wvm_id=${wvmId}&segment_id=${segmentId}&cluster_id=${clusterId}`, {})
    : post(`${ONPOINT}/core/v1/word_high_value?wvm_id=${wvmId}&segment_id=${segmentId}`, {});

// Clients
export const getClients = async () => {
  const { data } = await get(`${ONPOINT_AUTH}/clients`);
  return data;
};

export const addClient = async (payload) => post(`${ONPOINT_AUTH}/service/addClient`, payload);

export const delClient = async (payload) => del(`${ONPOINT_AUTH}/service/delClient/${payload}`);

export const getClient = async (id) => get(`${ONPOINT_AUTH}/clients/${id}`);

export const fetchClientByTenant = async (tenant) => get(`${ONPOINT_AUTH}/tenants/${tenant}`);

export const editClient = async (clientId, client) => patch(`${ONPOINT_AUTH}/clients/${clientId}`, client);

// Users

export const getUsers = async (isActive = 'true') => {
  const { data } = await get(`${ONPOINT_AUTH}/users?isActive=${isActive}`);
  return data;
};

export const getUser = async (userId) => get(`${ONPOINT_AUTH}/users/${userId}`);

/* eslint-disable no-console */

// Register
export const register = async (payload) => {
  await post(`${ONPOINT_AUTH}/register`, payload);
};

export const addUser = async (user) => register(user);

export const editUser = async (userId, user) => patch(`${ONPOINT_AUTH}/users/${userId}`, user);

export const deactivateUser = async (userId) => patch(`${ONPOINT_AUTH}/users/${userId}`, { isActive: false });

export const activateUser = async (userId) => patch(`${ONPOINT_AUTH}/users/${userId}`, { isActive: true });
// Profile

export const fetchProfile = async () => get(`${ONPOINT_AUTH}/profile`);

// Datasets

export const fetchDataset = async (id, options = {}) => get(`${ONPOINT}/core/v1/datasets/${id}`, options);

export const fetchDatasets = async (query = {}) => {
  const { items } = await get(`${ONPOINT}/core/v1/datasets/?query=${JSON.stringify(query, null, 0)}&per_page=100`);
  return items;
};

export const updateDataset = async (id, payload) => patch(`${ONPOINT}/core/v1/datasets/${id}`, payload);

// Segments

export const fetchSegments = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/segments/?query=${JSON.stringify(query, null, 0)}&per_page=200`,
    options
  );
  return items;
};

export const fetchSegmentsExperimental = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/segments/list_experimental?query=${JSON.stringify(query, null, 0)}&per_page=200`,
    options
  );
  return items;
};

export const fetchSegment = async (id, options = {}) => get(`${ONPOINT}/core/v1/segments/${id}`, options);

export const fetchSegmentProps = async (id, props = [], rels = []) =>
  get(`${ONPOINT}/core/v1/segments/${id}/properties?props=${props.join(',')}&rels=${rels.join(',')}`);

export const updateSegment = async (id, payload) => patch(`${ONPOINT}/core/v1/segments/${id}`, payload);

export const fetchCluster = async (id) => get(`${ONPOINT}/core/v1/clusters/${id}`);

export const updateCluster = async (id, payload) => patch(`${ONPOINT}/core/v1/clusters/${id}`, payload);

export const checkDeleteSegment = async (id) => get(`${ONPOINT}/core/v1/segments/${id}/service/can_delete`);

export const deleteSegment = async (id) => del(`${ONPOINT}/core/v1/segments/${id}`);

export const deleteCluster = async (id) => del(`${ONPOINT}/core/v1/clusters/${id}`);

export const fetchEntityDownloadUrl = async (entityId, entity, id) => {
  const { data } = await get(`${ONPOINT}/core/v1/${entity}/${entityId}/download/${id}/get_download_url`);
  return data;
};

export const deleteEntityDownload = async (entityId, entity, id) =>
  del(`${ONPOINT}/core/v1/${entity}/${entityId}/download/${id}`);

export const setVisibilityEntityDownload = async (entityId, entity, id, hiddenByRole) =>
  patch(`${ONPOINT}/core/v1/${entity}/${entityId}/download/${id}`, { hiddenByRole });

// Thinkalike Models

export const fetchModels = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v2/thinkalike_model/?query=${JSON.stringify(query, null, 0)}&per_page=100`,
    options
  );
  return items;
};

export const fetchModel = async (id, options = {}) => get(`${ONPOINT}/core/v2/thinkalike_model/${id}`, options);

export const fetchModelStatus = async (id, query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v2/thinkalike_model/${id}/status?query=${JSON.stringify(query, null, 0)}&per_page=100`,
    options
  );
  return items;
};

export const fetchModelScoresDownloadUrl = async (id) => {
  const { data } = await post(`${ONPOINT}/core/v2/thinkalike_model/${id}/service/downloadTalScores`, {});
  return data;
};

export const fetchModelDownloadUrl = async (id, type) => {
  const { data } = await get(`${ONPOINT}/core/v2/thinkalike_model/${id}/service/download/${type}`);
  return data;
};

export const fetchModelInputDistributionDownloadUrl = async (id, type) => {
  const { data, status } = await get(`${ONPOINT}/core/v1/${type}s/${id}/service/downloadInputDist`);
  if (status === 202) return null;
  return data;
};

export const fetchModelLiftChartDownloadUrl = async (id) => {
  const { data, status } = await get(`${ONPOINT}/core/v2/thinkalike_model/${id}/service/downloadLiftChart`);
  if (status === 202) return null;
  return data;
};

/**
 * copyModelToClient
 * @param payload {import('../store/typings').ModelCopyPayload}
 * @returns {Promise<null|*>}
 */
export const copyModelToClient = async (payload) => post(`${ONPOINT_AUTH}/integrations/pinpoint/share_model`, payload);

export const addThinkalikeModel = async (payload) => post(`${ONPOINT}/core/v2/thinkalike_model`, payload);

export const addThinkalikeModelStatus = async (id, payload) =>
  post(`${ONPOINT}/core/v2/thinkalike_model/${id}/status`, payload);

export const addThinkalikeModelTarget = async (id, payload) =>
  post(`${ONPOINT}/core/v2/thinkalike_model/${id}/target`, payload);

export const updateThinkalikeModel = async (id, payload) => patch(`${ONPOINT}/core/v2/thinkalike_model/${id}`, payload);

export const checkDeleteThinkalikeModel = async (id) =>
  get(`${ONPOINT}/core/v2/thinkalike_model/${id}/service/can_delete`);

export const deleteThinkalikeModel = async (id) => del(`${ONPOINT}/core/v2/thinkalike_model/${id}`);

export const fetchModelStats = async (payload) =>
  post(`${ONPOINT_AUTH}/integrations/mlflow/retrieve_model_stats`, payload);

export const validateReplacedOutcomes = async (payload) =>
  post(`${ONPOINT}/core/v2/thinkalike_model/service/validate_outcomes`, payload);

// RoiCalculator
export const saveRoiCalculatorState = async (payload) => post(`${ONPOINT}/core/v1/roi_calculator`, payload);

export const exportRoiCalculator = async (payload) =>
  post(`${ONPOINT_AUTH}/integrations/pinpoint/export_roi_calc`, payload);

// Subscriptions

export const fetchPossibleClSubs = async () => get(`${ONPOINT_AUTH}/integrations/pinpoint/psbl_cl_subs`);

export const fetchSubscribed = async (query = {}, options = {}) => {
  const { items } = await get(
    `${ONPOINT}/core/v1/notification/subscribe?query=${JSON.stringify(query, null, 0)}&per_page=1000`,
    options
  );
  return items;
};

export const fetchSubGroup = async (groupId) => get(`${ONPOINT}/core/v1/notification/subscribe/group/${groupId}`);

export const addSubscriber = async (payload) => post(`${ONPOINT}/core/v1/notification/subscribe`, payload);

export const fetchSubscriber = async (subId) => get(`${ONPOINT}/core/v1/notification/subscribe/${subId}`);

export const delSubscriber = async (subId) => del(`${ONPOINT}/core/v1/notification/subscribe/${subId}`);

// Onboardings

export const fetchOnboardings = async (query = {}, options = {}, pagination = {}) => {
  const params = [];
  if (Object.keys(query).length > 0) params.push(['query', JSON.stringify(query)]);
  if (Object.keys(pagination).length > 0) Object.keys(pagination).forEach((key) => params.push([key, pagination[key]]));
  const endpoint = `${ONPOINT}/onboarding/v1/onboarding?${new URLSearchParams(params).toString()}`;
  return get(endpoint, options);
};

export const fetchOnboardingConfigTypes = async (query = {}) => {
  const { items } = await get(
    `${ONPOINT}/onboarding/v2/onboarding_configs/type?query=${JSON.stringify(query, null, 0)}&per_page=100`
  );
  return items;
};

export const fetchClientOnboardingConfig = async (type, scopeClientType, tenant) => {
  const { config: configObject } = await get(
    `${ONPOINT}/onboarding/v2/onboarding_configs/${type}/${scopeClientType}/${tenant}`
  );
  return configObject;
};

export const fetchOnboardingConfigs = async (query = {}) => {
  const { items } = await get(
    `${ONPOINT}/onboarding/v2/onboarding_configs/?query=${JSON.stringify(query, null, 0)}&per_page=100`
  );
  return items;
};

export const updateOnboardingConfig = async (id, onboardingConfig) =>
  patch(`${ONPOINT}/onboarding/v2/onboarding_configs/${id}`, onboardingConfig);

export const addOnboardingConfig = async (onboardingConfig) =>
  post(`${ONPOINT}/onboarding/v2/onboarding_configs`, onboardingConfig);

export const getOnboarding = async (id, options = {}) => get(`${ONPOINT}/onboarding/v1/onboarding/${id}`, options);

export const getUploadFile = async (id, options = {}) => get(`${ONPOINT}/onboarding/v1/upload_files/${id}`, options);

export const addOnboardingMetadata = async (payload) =>
  post(`${ONPOINT}/onboarding/v1/upload_file_user_payload`, payload);

export const triggerDetWorkflow = async (payload) => post(`${ONPOINT}/onboarding/v1/workflows/trigger/det`, payload);

export const approveDetWorkflow = async (uploadFileId) =>
  post(`${ONPOINT}/onboarding/v1/workflows/approve/det/${uploadFileId}`, {});

export const confirmDetWorkflow = async (uploadFileId) =>
  post(`${ONPOINT}/onboarding/v1/workflows/clear/det/${uploadFileId}`, {});

// Submit Feedback

export const submitFeedback = async (payload) => post(`${ONPOINT_AUTH}/integrations/pinpoint/feedback`, payload);

// S3

export const getFileUploadUrl = async (bucket, key) => {
  const { data } = await post(`${ONPOINT_AUTH}/integrations/dot/generatePresignedUrl`, { bucket, key });
  return data;
};

export const getFileDownloadUrl = async (bucket, key) => {
  const { data } = await post(`${ONPOINT_AUTH}/integrations/dot/generateDownloadUrl`, { bucket, key, download: true });
  return data;
};

export const uploadFilePII = async (url, file, onProgress) => uploadFile(url, file, onProgress);

export const uploadFileLowSec = async (url, file, onProgress) => uploadFile(url, file, onProgress, true);
